import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Badge, Breadcrumb, BreadcrumbItem, Button, Col, Grid, MenuItem, NavDropdown, Row } from 'react-bootstrap';
import { FiArrowDown } from 'react-icons/fi';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import Spinner from 'react-spinkit';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes, tabKeys } from '../../../../constants';
import { fullName, isBusinessAdmin, isTeamManager } from '../../../../services';
import { businessActions, businessSelector, businessTeamsWithProgressSelector, fullTeamPendingExternalCpdSelector, queryTeamMembersSelector, teamMembersSelector } from '../../../../state/modules/business';
import { taskActions } from '../../../../state/modules/task';
import { taskCountSelector } from '../../../../state/modules/task/selectors';
import { currentUserSelector, userAfslSelector } from '../../../../state/modules/user';
import { pluralise, unfixedFloor } from '../../../../utils';
import { Icon, SortableTable, TeamAvatar } from '../../../components';
import SearchForm from '../../../components/search-form';
import { ProgressChart } from '../../../partials';
import Loading from '../../loading';
import NumberBadge from '../partials/number-badge';

const TeamHeader = ({ isBusinessAdmin }) => (
  <section id='header--teams' key='team-header' className='layout--header'>
    <Grid>
      <Row className='button-gutter'>
        <Col xs={12} md={6}>
          <Breadcrumb>
            <BreadcrumbItem active>
              Teams
            </BreadcrumbItem>
          </Breadcrumb>
          <h1>
            <i className='icon-team-large' /> Teams
          </h1>
          <hr />
        </Col>
        {
          isBusinessAdmin ?
            <Col xs={12} md={3} mdOffset={0}>
              <LinkContainer className='large full' to={routes.app.usersAndTeamsSettings(tabKeys.usersAndTeamsSettings.teams)}>
                <Button>
                  Manage Teams
                  </Button>
              </LinkContainer>
            </Col>
           : null
        }
         {
          isBusinessAdmin ?
            <Col xs={12} md={3} mdOffset={0}>
              <LinkContainer className='large full' to={routes.app.teams.newTeam}>
                <Button>
                  <Icon icon='add-large' /> Create new team
                </Button>
              </LinkContainer>
            </Col>
             : null
        }
      </Row>
    </Grid>
  </section>
);

const TeamColumns = [
  {
    name: 'teamName',
    display: () => 'Team name',
    className: () => 'team-name',
    sortable: true
  },
  {
    name: 'teamMembers',
    display: () => 'Team members',
    className: () => '',
    sortable: true
  },
  {
    name: 'teamManager',
    display: () => 'Team manager',
    className: () => ''
  },
  {
    name: 'progress',
    display: () => 'CPD completion',
    className: () => '',
    sortable: true
  },
  {
    name: 'cpdReview',
    display: () => 'CPD review',
    className: () => 'cpdReview'
  },
  {
    name: 'seeUsers',
    display: () => '',
    className: () => 'operations'
  }
];

const seeUsers = (routes) =>
  <LinkContainer
    className='pull-right'
    to={routes}>
    <Button bsStyle='plain'><Icon icon='chevron-right-large' /></Button>
  </LinkContainer>

const TeamDetails = branch(
  ({ teamsWithProgress }) => !teamsWithProgress,
  renderComponent(Loading)
)(({
  rows,
  businessProgress,
  businessSize,
  totalTeamsCount,
  taskCount,
  uniqueTeamPendingExternalCpd,
  loadMoreButton,
  searchQuery,
  updateSearchQuery,
  sortBy,
  isFetchingTeamsWithProgress,
  isBusinessAdmin
}) => (
    <section id='section--teams' className='layout--section'>
      <div className='container'>
        {
          isBusinessAdmin ?
            <div className='progress--banner'>
            <Row>
              <Col xs={12} md={2} className='banner-item'>
                <div className='chart'>
                  <ProgressChart progressPercentage={businessProgress} width={120} height={120} />
                </div>
                <div className='info'>
                  <p>Your <span>organisation's</span> overall CPD progress</p>
                </div>
              </Col>
              <Col xs={12} md={2} className='banner-item'>
                <LinkContainer className='clickable' to={routes.app.teams.allTeamMembers}>
                  <div className='chart'>
                    <NumberBadge value={businessSize} width={120} height={120} />
                  </div>
                </LinkContainer>
                <LinkContainer className='info clickable' to={routes.app.teams.allTeamMembers}>
                  <div>
                    <p>{pluralise('CPD Member', businessSize)}</p>
                  </div>
                </LinkContainer>
              </Col>
              <Col xs={12} md={2} className='banner-item'>
                <div className='chart'>
                  <NumberBadge value={totalTeamsCount} width={120} height={120} />
                </div>
                <div className='info'>
                  <p>{pluralise('Team', totalTeamsCount)}</p>
                </div>
              </Col>
              <Col xs={12} md={2} className='banner-item'>
                <LinkContainer className='chart clickable' to={routes.app.externalCpd.review}>
                  <div>
                    <Icon className='teams-icon' icon='cpd-activity-large' />
                    <Badge bsClass={`teams-notification ${uniqueTeamPendingExternalCpd && uniqueTeamPendingExternalCpd.length > 0 ? 'teams-badge-red' : 'teams-badge-green'}`}>
                      {
                        uniqueTeamPendingExternalCpd && uniqueTeamPendingExternalCpd.length > 0 ?
                          uniqueTeamPendingExternalCpd.length < 100 ? uniqueTeamPendingExternalCpd.length : '99+'
                          : <Icon icon='accept-thick' />
                      }
                    </Badge>
                  </div>
                </LinkContainer>
                <LinkContainer className='info clickable' to={routes.app.externalCpd.review}>
                  <div>
                    <p>
                      {
                        uniqueTeamPendingExternalCpd && uniqueTeamPendingExternalCpd.length > 0 ?
                          `Review ${uniqueTeamPendingExternalCpd.length} CPD ${pluralise('item', uniqueTeamPendingExternalCpd.length)}` : 'No CPD for review'
                      }
                    </p>
                  </div>
                </LinkContainer>
              </Col>
              <Col xs={12} md={2} className='banner-item'>
                <LinkContainer className='chart clickable' to={routes.app.tasks.home}>
                  <div>
                    <Icon className='teams-icon' icon='training-large' />
                    <Badge bsClass='teams-notification teams-task teams-badge-green'>
                      {taskCount > 0 ? taskCount < 100 ? taskCount : '99+' : <Icon icon='accept-thick' />}
                    </Badge>
                  </div>
                </LinkContainer>
                <LinkContainer className='info clickable' to={routes.app.tasks.home}>
                  <div>
                    <p>
                      {
                        taskCount > 0 ? `See ${taskCount} active ${pluralise('task', taskCount)}` : 'No active tasks'
                      }
                    </p>
                  </div>
                </LinkContainer>
              </Col>
              <Col xs={12} md={2} className='banner-item'>
                <LinkContainer className='chart clickable' to={routes.app.teams.allTeamProgressReport}>
                  <div>
                    <Icon className='teams-icon' icon='report-download' />
                    <Badge bsClass='teams-notification teams-badge-green'><FiArrowDown /></Badge>
                  </div>
                </LinkContainer>
                <LinkContainer className='info clickable' to={routes.app.teams.allTeamProgressReport}>
                  <div>
                    <p>
                      Download progress report
                    </p>
                  </div>
                </LinkContainer>
              </Col>
            </Row>
          </div>
          : null
        }
        <Row>
          <Col xs={12} md={6}>
            <h2>Your teams</h2>
          </Col>
          <Col xs={12} md={6}>
            <SearchForm id='search-desktop' label='Search Team' className='searchDesktop' searchQuery={searchQuery} onSearchChange={updateSearchQuery} />
          </Col>
        </Row>
        <SortableTable
          isFetchingAll={isFetchingTeamsWithProgress && fp.isEmpty(rows)}
          className='table--teams'
          rows={rows}
          sortBy={sortBy}
          emptyData={totalTeamsCount === 0 ? 'You have no teams' : 'No teams found'}
          columns={uniqueTeamPendingExternalCpd && uniqueTeamPendingExternalCpd.length > 0 ? TeamColumns : fp.remove(({ name }) => name === 'cpdReview')(TeamColumns)} />
        {loadMoreButton}
      </div>
    </section >
  ));

const TeamPage = (props) => (
  <div className='base-layout'>
    <TeamHeader key='team-header' {...props} />
    <TeamDetails key='team-details' {...props} />
  </div>
);

const defaultTake = 20;

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  business: businessSelector(state),
  teamPendingExternalCpd: fullTeamPendingExternalCpdSelector(state),
  isFetchingPendingExternalCpd: state.business.isFetchingPendingExternalCpd,
  queryMembersResults: queryTeamMembersSelector(state),
  businessAdmins: fp.filter(isBusinessAdmin)(teamMembersSelector(state)),
  afsLicence: userAfslSelector(state),
  teamsWithProgress: businessTeamsWithProgressSelector(state),
  taskCount: taskCountSelector(state),
  isFetchingTeamsWithProgress: state.business.isFetchingTeamsWithProgress
});

const mapDispatchToProps = (dispatch) => ({
  getTeamsWithProgress: bindActionCreators(businessActions.business.team.getTeamsWithProgress, dispatch),
  loadMoreTeamsWithProgress: bindActionCreators(businessActions.business.team.loadMoreTeamsWithProgress, dispatch),
  getFullTeamMembers: bindActionCreators(businessActions.business.queryTeamMembers, dispatch),
  fullTeamPendingExternalCpd: bindActionCreators(businessActions.business.getFullTeamPendingExternalCpd, dispatch),
  getTaskCount: bindActionCreators(taskActions.task.getCount, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    ({ business }) => !business,
    renderComponent(Loading)
  ),
  lifecycle({
    componentDidMount() {
      const {
        getTeamsWithProgress,
        business,
        getFullTeamMembers,
        fullTeamPendingExternalCpd,
        isFetchingPendingExternalCpd,
        getTaskCount,
        user
      } = this.props

      getTaskCount();

      getTeamsWithProgress(business.id, {
        skip: 0,
        take: defaultTake,
        selectedOnly: isBusinessAdmin(user) ? false : true,
      });
      getFullTeamMembers(business.id, {});

      if (business && !isFetchingPendingExternalCpd) {
        fullTeamPendingExternalCpd(business.id);
      }
    }
  }),
  branch(
    ({ queryMembersResults, teamsWithProgress }) => !queryMembersResults || !teamsWithProgress,
    renderComponent(Loading)
  ),
  withProps(
    ({ user }) => ({
      isBusinessAdmin: isBusinessAdmin(user),
    })
  ),
  withProps(
    ({ queryMembersResults }) => ({
      teamMembers: fp.get('teamMembers')(queryMembersResults),
      businessSize: fp.get('total')(queryMembersResults),
      businessProgress: fp.get('totalProgress')(queryMembersResults),
    })
  ),
  withStateHandlers(() => (
    {
      searchQuery: '',
      searchDebounce: undefined,
      sort: undefined
    }), {
      updateSearchDebounce: () => (searchDebounce) => ({ searchDebounce }),
      updateSearchQuery: () => (e) => ({ searchQuery: e.target.value }),
      updateSort: () => (sort) => ({ sort })
    }
  ),
  withProps(
    ({ teamMembers, user, isBusinessAdmin, searchQuery, sort, filter }) => ({
      filter: {
        ...filter,
        skip: 0,
        take: defaultTake,
        tags: searchQuery,
        sort,
        selectedOnly: isBusinessAdmin? false: true,
        selectedIds: isBusinessAdmin? undefined : teamMembers.filter(
          (member) => member.user.id === user.id
        )[0].isManagerTeamIDs.toString()
      }
    })
  ),
  lifecycle({
    componentDidMount() {
      const {
        getTeamsWithProgress,
        business,
        filter
      } = this.props

      getTeamsWithProgress(business.id, filter);
    }
  }),
  withProps(({ teamPendingExternalCpd }) => ({
    uniqueTeamPendingExternalCpd: fp.uniqBy('externalCpdId')(teamPendingExternalCpd)
  })),
  withProps(({ teamsWithProgress}) => ({
    businessTeams: fp.get('businessTeamsWithProgress')(teamsWithProgress),
    totalTeamsCount: fp.get('total')(teamsWithProgress),
    hasMoreToLoad: fp.get('businessTeamsWithProgress.length')(teamsWithProgress) < fp.get('hit')(teamsWithProgress),
  })),
  withHandlers({
    sortBy: ({ updateSort }) => (field, sortOrder) => {
      const sortParam = sortOrder ? `${field},${sortOrder}` : undefined;
      updateSort(sortParam);
    },
    loadMoreTeamsWithProgress: ({ loadMoreTeamsWithProgress, businessTeams, business, filter }) => () => {
      const query = {
        ...filter,
        skip: fp.get('length')(businessTeams),
      }
      loadMoreTeamsWithProgress(business.id, query)
    },
    refetchTeams: ({ getTeamsWithProgress, filter, business }) => () => getTeamsWithProgress(business.id, filter)
  }),
  lifecycle({
    componentWillReceiveProps({
      filter,
      refetchTeams,
      searchDebounce,
      updateSearchDebounce
    }) {
      if (!fp.isEqual(this.props.filter, filter)) {
        const debounced = fp.debounce(500)(refetchTeams)
        updateSearchDebounce(debounced)
        searchDebounce && searchDebounce.cancel()
        debounced()
      }
    }
  }),
  withHandlers({
    getTeamManagers: ({ teamMembers, businessAdmins }) => (teamId) => {
      const teamManagers = fp.flow(
        fp.filter((teamMember) => fp.includes(teamId)(teamMember.isManagerTeamIDs)),
        fp.map((teamMember) => fullName(teamMember.user))
      )(teamMembers);
      const businessManagers = fp.map((admin) => fullName(admin.user))(businessAdmins);
      const managers = teamManagers.length > 0 ? teamManagers : businessManagers;
      return managers.length > 1 ?
        (
          <div>
            <NavDropdown id='managers-dropdown' title='Multiple' className='dropdown-list' noCaret={true}>
              {
                fp.flow(
                  fp.sortBy((manager) => manager),
                  (sorted) => {
                    return sorted.map(
                      (manager, index) => <MenuItem key={index} className="unclickable">
                        <Button
                          bsStyle='plain'
                          className='menu-item'>
                          {manager}
                        </Button>
                      </MenuItem>
                    )
                  }
                )(managers)
              }
            </NavDropdown>
          </div>
        ) : fp.head(managers)
    },
    pendingCpdCount: ({
      uniqueTeamPendingExternalCpd,
      teamMembers,
      user,
      business
    }) => (teamId) => fp.flow(
      fp.filter(({ teamIDs }) => fp.includes(teamId)(teamIDs)),
      fp.map(({ user }) => user.id),
      // Filter out self pending cpd when the user is team manager since team manager cannot approve self cpd.
      fp.filter((userId) => isTeamManager(user, business) ? userId !== user.id : true),
      (userIds) => fp.filter(({ userId }) => fp.includes(userId)(userIds))(uniqueTeamPendingExternalCpd),
      fp.size
    )(teamMembers)
  }),
  withProps(
    ({
      businessTeams,
      getTeamManagers,
      pendingCpdCount,
      businessSize,
      businessProgress,
      uniqueTeamPendingExternalCpd,
      isBusinessAdmin
    }) => ({
      rows: fp.flow(
        fp.map((businessTeam) => ({
          teamName: <LinkContainer to={routes.app.teams.teamMembers(businessTeam.team.id)}>
            <div>
              <TeamAvatar teamName={businessTeam.team.name} />
              <span>{he.decode(businessTeam.team.name)}</span>
            </div>
          </LinkContainer>,
          teamMembers: `${businessTeam.teamSize} ${pluralise('member', businessTeam.teamSize)}`,
          teamManager: getTeamManagers(businessTeam.team.id),
          progress: `${unfixedFloor(businessTeam.progress, 0)}%`,
          cpdReview: pendingCpdCount(businessTeam.team.id) > 0 ?
            <LinkContainer to={routes.app.externalCpd.reviewTeam(businessTeam.team.id)}>
              <Badge bsClass='team-notification'>{pendingCpdCount(businessTeam.team.id) > 99 ? '99+' : pendingCpdCount(businessTeam.team.id)}</Badge>
            </LinkContainer> : null,
          seeUsers: seeUsers(routes.app.teams.teamMembers(businessTeam.team.id))
        })),
        (businessTeamRows) => {
          if (!isBusinessAdmin) {
            return businessTeamRows;
          }
          const allMembersRow = {
            teamName: <LinkContainer to={routes.app.teams.allTeamMembers}>
              <div>
                <TeamAvatar teamName='All CPD Members' />
                <span>{'All CPD Members'}</span>
              </div>
            </LinkContainer>,
            teamMembers: `${businessSize} ${pluralise('member', businessSize)}`,
            teamManager: '',
            progress: `${unfixedFloor(businessProgress, 0)}%`,
            cpdReview: uniqueTeamPendingExternalCpd.length > 0 ?
              <LinkContainer to={routes.app.externalCpd.review}>
                <Badge bsClass='team-notification'>{uniqueTeamPendingExternalCpd.length > 99 ? '99+' : uniqueTeamPendingExternalCpd.length}</Badge>
              </LinkContainer> : null,
            seeUsers: seeUsers(routes.app.teams.allTeamMembers)
          }
          return [allMembersRow].concat(businessTeamRows)
        }
      )(businessTeams)
    })
  ),
  withProps(
    ({
      hasMoreToLoad,
      isFetchingTeamsWithProgress,
      loadMoreTeamsWithProgress
    }) => ({
      loadMoreButton: hasMoreToLoad ? <Row>
        <Col xs={4} xsOffset={4}>
          {
            isFetchingTeamsWithProgress ?
              <Button><Spinner name='circle' className='spinner' fadeIn='none' /></Button> :
              <Button className='full' onClick={loadMoreTeamsWithProgress}>Load More</Button>
          }
        </Col>
      </Row> : null
    })
  )
)(TeamPage)



// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/teams.js